import React from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Leaf } from "lucide-react";
import { useT } from "@/lib/LanguageContext";
import { useSiteCopy } from "@/lib/useSiteCopy";
import { tenantHref } from "@/lib/tenantPath";
import { useTenant } from "@/lib/TenantContext";
import { resolveBrandName } from "@/lib/siteLinks";
// Relative, not "@/": Hero.fillerText.test.js mounts Hero directly and only virtual-mocks
// the handful of "@/" modules it needs (see that file's header) — an unmocked "@/" import
// here would fail to resolve under Jest (craco.config.js: no jest moduleNameMapper for "@/").
import { hasText } from "../../lib/siteContent";

const Hero = () => {
  // siteCopy = dictionary + Site Content overrides + the tenant record (lib/useSiteCopy.js).
  const siteCopy = useSiteCopy();
  const tt = useT("templates");
  const { tenant } = useTenant();
  // The portrait's alt text and the credential badge were literal strings — "Émilie Houde"
  // and the Quebec designation "ND.A", i.e. the SEED tenant's name and qualification,
  // published on every practitioner's site as if they were theirs. Neither was reachable
  // from Site Content, so no tenant could correct it. Both now come from the tenant record;
  // the designation row simply disappears for a practice that has not set a specialty.
  const brandName = resolveBrandName(tenant, tt("shared.brand.fallback"));
  const specialty = tenant?.specialty || "";
  const hero = siteCopy.hero;
  const heroBg = siteCopy.images.heroBg;
  const heroPortrait = siteCopy.images.heroPortrait;
  const { scrollY } = useScroll();
  const yImg = useTransform(scrollY, [0, 600], [0, 80]);
  const yText = useTransform(scrollY, [0, 600], [0, -30]);
  const opacity = useTransform(scrollY, [0, 400], [1, 0.4]);

  return (
    <section id="top" data-testid="hero-section" className="relative pt-32 md:pt-40 pb-20 md:pb-28 overflow-hidden bg-eh-bg">
      {/* Brand-aligned naturopathy desk BG — plants, tinctures, herbs */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: `url('${heroBg}')`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          opacity: 1,
        }}
      />
      {/* Sage tint stronger on text side, fading toward portrait */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(105deg, rgba(201,210,189,0.78) 0%, rgba(201,210,189,0.55) 28%, rgba(201,210,189,0.25) 55%, rgba(244,239,226,0.08) 80%, rgba(244,239,226,0.02) 100%)",
        }}
      />
      <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <motion.div style={{ y: yText, opacity }} className="lg:col-span-7 relative z-10">
          {/* BUG-210: no dictionary sample text backs this any more (lib/content.js) — it
              names the practitioner's title and region, which this file cannot invent
              without stating something false about whoever hasn't set it. Same guard idiom
              as the specialty badge below (hero-badge-specialty). */}
          {hasText(hero.eyebrow) && (
            <div className="mb-6 flex items-center gap-2">
              <span className="chip" data-testid="hero-eyebrow">
                <span className="dot" /> {hero.eyebrow}
              </span>
            </div>
          )}

          {/* One title, a line per line break (lib/siteContent.js `titleLines`). The second
              line keeps the design's italic accent, whatever the practitioner wrote. */}
          <h1 className="font-display text-[3.4rem] sm:text-6xl md:text-7xl lg:text-[5.6rem] leading-[0.95] tracking-[-0.02em] text-eh-ink">
            {hero.titleLines.map((line, i) => (
              <motion.span
                key={i}
                initial={{ y: 60, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.9, delay: 0.08 * i, ease: [0.16, 1, 0.3, 1] }}
                className={i === 1 ? "block italic text-eh-sage-deep" : "block"}
                data-testid="hero-title-line"
              >
                {line}
              </motion.span>
            ))}
          </h1>

          {/* Hidden when the practitioner saved it empty (an `emptyHides` slot). */}
          {hasText(hero.script) && (
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.4 }}
              className="font-script text-3xl md:text-4xl text-eh-ochre-deep mt-6"
              data-testid="hero-script"
            >
              {hero.script}
            </motion.p>
          )}

          {hasText(hero.sub) && (
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.5 }}
              className="mt-8 text-lg md:text-xl text-eh-muted max-w-xl leading-relaxed"
              data-testid="hero-sub"
            >
              {hero.sub}
            </motion.p>
          )}

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.6 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a href={tenantHref("/reserver")} data-testid="hero-primary-cta" className="btn-primary">
              {hero.cta} <ArrowRight size={18} />
            </a>
            <a href="#approach" data-testid="hero-secondary-cta" className="btn-ghost">
              {hero.ctaSecondary}
            </a>
          </motion.div>

          {/* BUG-210: stat1/stat1Label/stat2/stat2Label/stat3/stat3Label have no dictionary
              sample any more (lib/content.js) — a practice's hours of training, years in
              practice or city/region cannot be invented. An unset field is simply "", so an
              unset pair degrades to a value AND a label that are both empty together —
              never a lone label or a lone "—" — rather than filler text. The row itself
              (BUG-196/197's `hero-stats`, tested independently) stays structurally in
              place; the tenant's OWN copy of a set stat still renders exactly as before. */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.9 }}
            data-testid="hero-stats"
            // BUG-196: an unprefixed `grid-cols-3` forced three columns at every
            // width, including a ~430px phone. Single column below `sm`, the
            // original 3-up row restored from `sm:` (640px) up.
            className="mt-14 grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-lg"
          >
            {[
              { v: hero.stat1, l: hero.stat1Label },
              { v: hero.stat2, l: hero.stat2Label },
              { v: hero.stat3, l: hero.stat3Label },
            ].map((s, i) => (
              <div key={i} className="border-l border-eh-border pl-4">
                <div className="font-display text-2xl md:text-3xl text-eh-ink">{s.v}</div>
                <div className="text-xs uppercase tracking-widest text-eh-muted mt-1">{s.l}</div>
              </div>
            ))}
          </motion.div>
        </motion.div>

        <motion.div
          style={{ y: yImg }}
          initial={{ opacity: 0, scale: 1.04 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-5 relative"
        >
          <div className="relative">
            <div className="absolute -top-10 -left-10 w-40 h-40 rounded-full bg-eh-ochre/20 blur-2xl" />
            <div className="absolute -bottom-10 -right-10 w-48 h-48 rounded-full bg-eh-sage/30 blur-3xl" />
            <div className="relative rounded-[2.5rem] overflow-hidden shadow-[0_24px_60px_-20px_rgba(44,53,49,0.35)] vignette grain">
              <img
                src={heroPortrait}
                alt={brandName}
                className="w-full h-[560px] md:h-[660px] object-cover object-center"
                data-testid="hero-portrait"
              />
            </div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 1 }}
              className="absolute -left-6 md:-left-10 bottom-10 bg-eh-cream border border-eh-border-soft rounded-2xl p-4 shadow-[0_12px_30px_-10px_rgba(44,53,49,0.18)] max-w-[230px]"
            >
              <div className="flex items-center gap-2 text-eh-sage-deep">
                <Leaf size={16} />
                {specialty && <span className="eyebrow" data-testid="hero-badge-specialty">{specialty}</span>}
              </div>
              <div className="font-display text-xl text-eh-ink mt-1 leading-tight" data-testid="hero-badge-brand">{brandName}</div>
              {/* BUG-210: no dictionary sample any more — same guard idiom as the specialty
                  badge above (hero-badge-specialty). */}
              {hasText(hero.credential) && <div className="text-xs text-eh-muted mt-1">{hero.credential}</div>}
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
